import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, borderRadius, shadows, spacing } from '../../theme/tokens';

export enum AvatarSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  EXPERT = 'expert'
}

export enum AvatarStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
  AWAY = 'away',
  BUSY = 'busy'
}

export enum AvatarVariant {
  ROUND = 'round',
  ROUNDED = 'rounded'
}

interface AvatarProps {
  size?: AvatarSize;
  variant?: AvatarVariant;
  src?: string;
  alt?: string;
  name?: string;
  status?: AvatarStatus;
  onClick?: () => void;
  className?: string;
  withBorder?: boolean;
  withShadow?: boolean;
  marginRight?: string;
}

const sizeMap = {
  [AvatarSize.XS]: '24px',
  [AvatarSize.SM]: '32px',
  [AvatarSize.MD]: '40px',
  [AvatarSize.LG]: '48px',
  [AvatarSize.XL]: '64px',
  [AvatarSize.EXPERT]: '56px'
};

const fontSizeMap = {
  [AvatarSize.XS]: typography.fontSize.xs,
  [AvatarSize.SM]: typography.fontSize.sm,
  [AvatarSize.MD]: typography.fontSize.md,
  [AvatarSize.LG]: typography.fontSize.lg,
  [AvatarSize.XL]: typography.fontSize.xl,
  [AvatarSize.EXPERT]: typography.fontSize.lg
};

const statusColors = {
  [AvatarStatus.ONLINE]: colors.status.online,
  [AvatarStatus.OFFLINE]: colors.status.offline,
  [AvatarStatus.AWAY]: colors.status.away,
  [AvatarStatus.BUSY]: colors.danger.main
};

// Glowing border styles based on status
const getStatusBorderStyles = (status?: AvatarStatus) => {
  if (!status) return '';
  
  switch (status) {
    case AvatarStatus.ONLINE:
      return css`
        border: 2.5px solid #12a571;
        box-shadow: 0 0 12px rgba(18, 165, 113, 0.5), 0 0 20px rgba(18, 165, 113, 0.3);
      `;
    case AvatarStatus.BUSY:
      return css`
        border: 2.5px solid #e5484d;
        box-shadow: 0 0 12px rgba(229, 72, 77, 0.5), 0 0 20px rgba(229, 72, 77, 0.3);
      `;
    case AvatarStatus.AWAY:
      return css`
        border: 2.5px solid #ef9b61;
        box-shadow: 0 0 12px rgba(239, 155, 97, 0.5), 0 0 20px rgba(239, 155, 97, 0.3);
      `;
    case AvatarStatus.OFFLINE:
    default:
      return css`
        border: 2.5px solid #e0e8f5;
        box-shadow: none;
      `;
  }
};

// Get background color based on status
const getStatusBackgroundColor = (status?: AvatarStatus) => {
  if (!status) return colors.primary.main;
  switch (status) {
    case AvatarStatus.ONLINE:
      return colors.primary.main; // Blue/primary when online
    case AvatarStatus.OFFLINE:
      return '#65748d'; // Gray when offline
    case AvatarStatus.AWAY:
      return '#7b8aa3'; // Lighter gray when away
    case AvatarStatus.BUSY:
      return colors.danger.main; // Red when busy
    default:
      return colors.primary.main;
  }
};

const StyledAvatar = styled.div<{ 
  size: AvatarSize; 
  variant: AvatarVariant; 
  clickable: boolean; 
  withBorder: boolean; 
  withShadow: boolean; 
  marginRight?: string;
  status?: AvatarStatus;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ size }) => sizeMap[size]};
  height: ${({ size }) => sizeMap[size]};
  border-radius: ${({ variant }) => 
    variant === AvatarVariant.ROUND ? borderRadius.round : borderRadius.xl};
  background-color: ${({ status }) => getStatusBackgroundColor(status)};
  color: ${colors.background.default};
  font-size: ${({ size }) => fontSizeMap[size]};
  font-weight: ${typography.fontWeight.medium};
  overflow: hidden;
  user-select: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  
  /* Apply status-based glowing border */
  ${({ status }) => status && getStatusBorderStyles(status)}
  
  /* Override with custom border if withBorder is true and no status */
  ${({ withBorder, status }) => withBorder && !status && css`
    border: 2px solid ${colors.border.light};
  `}
  
  ${({ withShadow, status }) => withShadow && !status && css`
    box-shadow: 0 4px 8px rgba(39, 68, 131, 0.07);
  `}
  
  ${({ marginRight }) => marginRight && css`
    margin-right: ${marginRight};
  `}
  
  ${({ clickable }) => clickable && css`
    cursor: pointer;
    transition: transform 0.2s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  `}
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-transform: uppercase;
`;

// StatusIndicator is no longer used - replaced with glowing border ring
// Keeping the component for potential future use or gradual migration
const StatusIndicator = styled.div<{ status: AvatarStatus; size: AvatarSize }>`
  display: none; /* Hidden - using glowing border instead */
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${({ size }) => {
    const avatarSize = parseInt(sizeMap[size]);
    return `${Math.round(avatarSize * 0.3)}px`;
  }};
  height: ${({ size }) => {
    const avatarSize = parseInt(sizeMap[size]);
    return `${Math.round(avatarSize * 0.3)}px`;
  }};
  background-color: ${({ status }) => statusColors[status]};
  border: 2px solid ${colors.background.default};
  border-radius: ${borderRadius.round};
`;

const getInitials = (name: string): string => {
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const Avatar: React.FC<AvatarProps> = ({
  size = AvatarSize.MD,
  variant = AvatarVariant.ROUND,
  src,
  alt = '',
  name = '',
  status,
  onClick,
  className,
  withBorder = false,
  withShadow = false,
  marginRight
}) => {
  const [imageError, setImageError] = useState(false);
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const showFallback = !src || imageError;
  const initials = name ? getInitials(name) : '?';
  
  return (
    <StyledAvatar
      size={size}
      variant={variant}
      clickable={!!onClick}
      withBorder={withBorder}
      withShadow={withShadow}
      marginRight={marginRight}
      onClick={onClick}
      className={className}
      status={status}
    >
      {showFallback ? (
        <AvatarFallback>{initials}</AvatarFallback>
      ) : (
        <AvatarImage
          src={src}
          alt={alt || name}
          onError={handleImageError}
        />
      )}
    </StyledAvatar>
  );
};

export default Avatar;